/**
 * stripBoilerplate — remove the noise sentences LLMs habitually emit
 * around real content.
 *
 * Background: even with explicit "no preamble, no Done — X confirmation"
 * rules in the system prompt, models leak phrases like "I'll sort the
 * data in ascending order" or "Done — 10 rows added" almost every turn.
 * The diff card already shows the user EXACTLY what changed; repeating
 * it in prose is pure attention tax. We strip it client-side so the
 * user sees only meaningful narrative (placement decisions, partial
 * results, honest "couldn't find X" admissions).
 *
 * Strategy:
 *   - Match conservative regex patterns at the START / END of the text.
 *   - Loop while removals happen, so stacked preambles ("Sure! I'll add
 *     data. Let me create the sheet.") collapse fully.
 *   - Never touch the middle of the text — content there is more likely
 *     to carry real info.
 *   - Empty result is fine; MessageBubble hides empty assistant bubbles
 *     when a diff card or tool calls already render below.
 *
 * Tested against common production examples:
 *   "I'll sort the data in ascending order."           → ""
 *   "Done — 10 labour records added."                  → ""
 *   "Sure! I'll add 10 rows. Done — added 10 rows."    → ""
 *   "10 rows on a fresh 'Labour' sheet — source untouched." → kept (real info)
 *   "Found 47 duplicates in column B."                 → kept (data answer)
 */

const PREAMBLE_PATTERNS: RegExp[] = [
  // "Sure! I'll <verb> <stuff>." — common acknowledgment + commitment
  /^\s*(?:Sure[,!.]?\s+|Of course[,.]?\s+|Certainly[,.]?\s+|Happy to help[,.]?\s+)?I['\u2019]?ll\b[^.!?\n]*[.!?]\s*/i,
  // "Let me <verb> ..."
  /^\s*Let me\b[^.!?\n]*[.!?]\s*/i,
  // "First/Now/Next/Then I'll ..."
  /^\s*(?:First|Now|Next|Then)[,.]?\s+I['\u2019]?ll\b[^.!?\n]*[.!?]\s*/i,
  // Bare politeness openers when used as standalone first sentence.
  /^\s*(?:Of course|Certainly|Sure thing|Sure[,!.]|Happy to help)[,.!]?\s*/i,
  // "I'll be happy to <verb> ..." / "I will <verb> ..." \u2014 but NOT
  // information-need statements ("I will need the file password"), which
  // carry real content the user must act on.
  /^\s*I\s+(?:will|'ll|\u2019ll)\s+(?:be\s+)?(?:happy\s+to\s+)?(?!need|require|want|have\s+to)\b[^.!?\n]*[.!?]\s*/i,
];

const POSTAMBLE_PATTERNS: RegExp[] = [
  // "Done — X added" / "Done: X created"
  /\b(?:Done|Finished|Complete)\s*[\u2014\u2013\-:.]?\s+[^.!?\n]*[.!?]/gi,
  // "Successfully X" sentences
  /\bSuccessfully\s+[^.!?\n]*[.!?]/gi,
  // "10 records added" / "Sheet created" / "Changes applied" — bare
  // confirmations. END-ANCHORED ($): only strip when this IS the trailing
  // sentence, so a mid-answer fact ("47 rows were inserted, see column B")
  // in a real analysis reply survives.
  /\b(?:\d+\s+)?(?:rows?|records?|changes?|cells?|sheets?)\s+(?:have\s+been\s+|has\s+been\s+|been\s+|were\s+)?(?:added|created|applied|saved|written|inserted|placed)\b[^.!?\n]*[.!?]\s*$/i,
  // "Click Apply ..." / "Approval do to apply"
  /\b(?:You\s+can\s+now\s+|Now you can\s+)?Click\s+Apply\b[^.!?\n]*[.!?]?\s*$/gi,
  /\bApproval\s+do\s+to\s+apply\b[^.!?\n]*[.!?]?\s*$/gi,
  // "X has been added/created/saved/applied" — passive confirmation,
  // end-anchored for the same reason as above.
  /\b(?:The\s+|Your\s+)?(?:[A-Z]\w*\s+){0,3}(?:has\s+been|have\s+been)\s+(?:added|created|applied|saved|written|inserted)\b[^.!?\n]*[.!?]\s*$/i,
  // "Check the X sheet" / "See the X sheet" — pointless when sheet name on diff
  /\b(?:Check|See)\s+the\s+["'][^"'\n]+["']\s+sheet\.?\s*$/gi,
];

// citationPattern matches a cell/range citation the model emits, e.g.
// [Sheet1!B15] or [Sheet1!B2:B30]. The sheet segment allows spaces and is
// bounded by "!"; the address is one or two A1 refs. We deliberately do
// NOT match tokens already followed by "(" so genuine markdown links
// [text](url) are left untouched.
const citationPattern =
  /\[([^\]!]+)!(\$?[A-Za-z]{1,3}\$?\d{1,7}(?::\$?[A-Za-z]{1,3}\$?\d{1,7})?)\](?!\()/g;

/**
 * linkifyCitations rewrites [Sheet!A1] citation tokens into markdown
 * links with a `cell:` scheme, e.g. [Sheet1!B15](cell:Sheet1|B15).
 * MessageBubble's `a` renderer intercepts `cell:` hrefs and calls
 * office.selectRange so the user jumps to the cited cell. The label keeps
 * the human-readable Sheet!Addr form.
 */
export function linkifyCitations(text: string): string {
  if (!text) return text;
  return text.replace(citationPattern, (_m, sheet: string, addr: string) => {
    const s = sheet.trim();
    return `[${s}!${addr}](cell:${encodeURIComponent(s)}|${encodeURIComponent(addr)})`;
  });
}

export function stripBoilerplate(text: string): string {
  if (!text) return text;
  let s = text;

  // Loop until no preamble pattern matches at start. Stacked
  // ("Sure! I'll add data. Let me create the sheet.") need multiple passes.
  let prev = '';
  while (prev !== s) {
    prev = s;
    for (const re of PREAMBLE_PATTERNS) {
      s = s.replace(re, '');
    }
    s = s.trimStart();
  }

  // Postamble patterns are global (any position) but in practice only
  // hit at the end. Run once.
  for (const re of POSTAMBLE_PATTERNS) {
    s = s.replace(re, '');
  }

  // Collapse whitespace artifacts left by the strips.
  s = s.replace(/\n{3,}/g, '\n\n');
  s = s.replace(/[ \t]{2,}/g, ' ');
  s = s.trim();

  return s;
}

/**
 * shouldHideAssistantBubble — when the only thing left in an assistant
 * message is boilerplate that's already been stripped, hide the bubble
 * entirely so the diff card / tool cards aren't followed by an empty
 * grey rectangle.
 *
 * Rules:
 *   - If text is non-empty after strip → SHOW (real info to convey)
 *   - If text is empty AND a diff or tool list exists → HIDE (those carry
 *     the meaning)
 *   - If text is empty AND nothing else exists → SHOW (don't render a
 *     ghost message; this is a fallback for "" assistant turn)
 */
export function shouldHideAssistantBubble(
  cleanedText: string,
  hasDiffOrTools: boolean,
): boolean {
  return cleanedText.length === 0 && hasDiffOrTools;
}
